/* eslint-disable @typescript-eslint/no-explicit-any */
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  AfterContentInit,
  Component,
  ContentChildren,
  effect,
  HostListener,
  input,
  Input,
  QueryList,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { Dropdown, DropdownModule } from 'primeng/dropdown';
import { InputText, InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { Nullable } from 'primeng/ts-helpers';
import { BehaviorSubject, combineLatest, debounceTime, map, shareReplay, startWith, switchMap } from 'rxjs';

import { SmartPickerHandler } from './smart-picker.handler';
import { AvatarComponent } from '../../components/avatar/avatar.component';
import { TemplateDirective } from '../../directives/template.directive';
import { cast } from '../../utils';
import { BaseSmartInputComponent } from '../smart-input-string/base-smart-input.component';
import { isPrintableCharacter } from 'src/app/utils/printable-char';

// export type EntityPickerModel<T> = {
//   id: string;
//   name: string;
//   picture?: string;
//   src: T;
// };

@Component({
  selector: 'app-smart-input-entity',
  templateUrl: './smart-input-entity.component.html',
  styleUrl: './smart-input-entity.component.scss',
  standalone: true,
  imports: [
    AsyncPipe,
    AvatarComponent,
    DropdownModule,
    FormsModule,
    InputTextModule,
    NgTemplateOutlet,
    ReactiveFormsModule,
    SvgIconComponent,
    TranslateModule,
    TooltipModule,
  ],
})
export class SmartInputEntityComponent<T extends { id: string }>
  extends BaseSmartInputComponent<T | undefined>
  implements AfterContentInit
{
  private _dropdown?: Dropdown;
  @ViewChild(Dropdown, { static: false }) set dropdown(dropdown: Dropdown) {
    if (dropdown) {
      this.handler().setDropdown(dropdown);
    }
    this._dropdown = dropdown;
  }
  get dropdown(): Dropdown | undefined {
    return this._dropdown;
  }

  private _searchInput!: InputText;
  @ViewChild(InputText, { static: false }) set searchInput(searchInput: InputText) {
    this._searchInput = searchInput;
  }

  @ContentChildren(TemplateDirective) templates: Nullable<QueryList<TemplateDirective>>;

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    // custom searchbox
    if (this._dropdown?.overlayVisible) {
      // in case of printable character, give the searchbox the focus
      if (isPrintableCharacter(event.key)) {
        this._searchInput.el.nativeElement.focus();
        // append the first char to the searchbox if empty
        if (!this.filterQuery.value?.length) {
          this.filterQuery.setValue(event.key);
          return;
        }
        return;
      } else {
        // propagate other event to the dropdown handler
        this._dropdown?.onKeyDown(event, false);
      }
    }
  }

  readonly handler = input.required<SmartPickerHandler<any>>();

  @Input() fieldLabel = 'name';
  @Input() showTooltip = false;

  private _needReset = false;

  itemTemplate?: TemplateRef<{ item: T }>;

  readonly cast = cast<T>;

  filterQuery = new FormControl('');

  private redrawSelectedItem$ = new BehaviorSubject<void>(undefined);

  readonly options$ = combineLatest([toObservable(this.handler), this.redrawSelectedItem$]).pipe(
    switchMap(([handler]) => {
      return handler.items$.pipe(startWith([]));
    }),
    map((items) => {
      const value = this.control().value;

      if (items === null) {
        return [value ? value : {}, {}, {}, {}, {}, {}];
      }
      const a = [
        // always put the selected item on top (selected item is excluded from the query)
        ...((items || []).some((i) => i.id === value?.id) || !value?.id ? [] : [value]),
        ...(items || []),
      ];
      return a;
    }),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  ngAfterContentInit(): void {
    (this.templates as QueryList<TemplateDirective>).forEach((item) => {
      switch (item.getType()) {
        case 'item':
          this.itemTemplate = item.template;
          break;

        default:
          this.itemTemplate = item.template;
          break;
      }
    });
  }

  constructor() {
    super();

    effect(() => {
      void this.control();
      this.redrawSelectedItem$.next();
    });

    // auto select
    effect(() => {
      const handler = this.handler();
      void this.autoSelect(handler);
    });

    effect(() => {
      const sub = this.control().valueChanges.subscribe(() => {
        this.redrawSelectedItem$.next();
      });
      return () => sub.unsubscribe();
    });

    // debounced query
    this.filterQuery.valueChanges.pipe(debounceTime(300)).subscribe((value) => this.filterChange(value ?? ''));

    // FIX: scroller height: when items are very few, the dropdown reduces its height.
    // If the items return to being many, the height doesn't adjust.
    // We add a css min height to enforce the right height (primeng calculate just the height property)
    effect(() => {
      const handler = this.handler();
      const sub = this.options$.subscribe((options) => {
        const scrollerEl = this._dropdown?.scroller?.getElementRef()?.nativeElement;
        if (scrollerEl) {
          const elements = options.length;
          const yPadding = 16;
          const defaultMinimumHeight = handler.options.templateOptions.scrollHeight + yPadding;
          const effectiveHeight = handler.options.templateOptions.itemSize * elements + yPadding;
          const minHeight = effectiveHeight < defaultMinimumHeight ? effectiveHeight : defaultMinimumHeight;
          scrollerEl.style.minHeight = `${minHeight}px`;
        }
      });

      return () => sub.unsubscribe();
    });
  }

  // Keep this out of the effect() above — an async arrow passed directly to
  // effect() inside a constructor crashes under Storybook's build (#600).
  private async autoSelect(handler: SmartPickerHandler<any>): Promise<void> {
    if (handler.options.preloadStrategy.autoSelectSingleChoice) {
      const singleChoice = await handler.getSingleChoice();
      if (singleChoice && this.control().value?.id !== singleChoice.id) {
        this.control().patchValue(singleChoice);
        return;
      }
    }
    if (handler.options.preloadStrategy.autoSelectFirstItem) {
      const firstChoice = await handler.getFirstChoice();
      if (firstChoice && !this.control().value) {
        this.control().patchValue(firstChoice);
        return;
      }
    }
  }

  filterChange(evt: string) {
    this.handler().search(evt);
  }

  onModelChange(item: T | undefined) {
    this.handler().setSelectedId(item?.id);

    this._needReset = true;
  }

  onShow() {
    this.handler().open();
  }

  onHide() {
    if (this._needReset) {
      this.handler().reset();
    }
    this._needReset = false;

    this.filterQuery.setValue('');
  }

  override onCancel() {
    if (this.control().pristine) {
      return;
    }
    const resetValue = this.resetValue();
    if (resetValue) {
      this.control().patchValue(resetValue);
      this.control().markAsPristine();
    }
    this.handler().reset();
  }
}
